import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, RefreshCcw } from "lucide-react";

export function NetworkStatus({ children }) {
  // Always true on SSR or initial load
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <>
      {children}
      <AnimatePresence>
        {!isOnline && (
          <motion.div 
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(12px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            className="fixed inset-0 z-[99999] bg-white/80 dark:bg-black/80 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-8 max-w-md w-full text-center shadow-2xl overflow-hidden relative"
            >
               {/* Center Icon */}
              <div className="relative z-10 mx-auto w-24 h-24 mb-6">
                <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping" />
                <div className="absolute inset-0 bg-red-500/10 rounded-full animate-pulse blur-xl" />
                <div className="relative h-full w-full bg-red-50 dark:bg-red-500/20 border border-red-500/30 rounded-full flex items-center justify-center backdrop-blur-md">
                  <WifiOff className="h-10 w-10 text-red-500 drop-shadow-lg" />
                </div>
              </div>

              <div className="relative z-10 space-y-3">
                <h2 className="text-3xl font-black italic tracking-tighter uppercase text-gray-900 dark:text-white">
                  Connection Lost
                </h2>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 max-w-[280px] mx-auto leading-relaxed">
                  We're unable to connect to the server. Please check your network connection and try again.
                </p>
              </div>

              <div className="relative z-10 mt-8">
                <button 
                  onClick={() => window.location.reload()} 
                  className="w-full h-12 flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs gap-2 group shadow-lg shadow-blue-600/20 transition-all active:scale-95"
                >
                  <RefreshCcw className="h-4 w-4 group-hover:-rotate-180 transition-transform duration-500" />
                  Retry Connection
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
